// frontend/src/components/Financeiro/FinanceiroExportacoes.js
// =============================================
// EXPORTAÇÕES PDF + CSV
// ✅ Cada relatório tem o seu PRÓPRIO modal de configuração
// ✅ Seleção de período: Mensal (mês/ano) | Trimestral (T1-T4/ano) | Anual (ano)
// ✅ Exportação CSV + PDF — AMBOS via backend (dados consistentes + cabeçalho de contexto)
// ✅ Design system unificado (--fin-*)
// 🔥 configuracao (regime+moeda+formatacao) + empresa vêm do endpoint
//    /api/financeiro/completo — sem chamadas a /api/empresas/minha/atual
// 🔥 FILTRAGEM POR REGIME FISCAL:
//    - "Apuramento IVA" — apenas para regime NORMAL
//    - "Apuramento ISPC" — apenas para regime ISPC (IVA 0%)
//    - Regime ISENTO — só Balancete, Aging e Despesas
// 🔥 DARK MODE: deteta via classe no <body>/<html> ou via localStorage
// 🔥 NOVO: hover em todos os botões + border-radius bonito nos dropdowns
//          + dropdowns com cor escura correta no dark mode
// =============================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Icon } from './SharedComponents';

const API = '/api' || 'http://localhost:3000';

const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const trimestreDoMes = (mes) => Math.floor(mes / 3) + 1;

const anoAtual = new Date().getFullYear();
const ANOS_DISPONIVEIS = Array.from({ length: 8 }, (_, i) => anoAtual - 5 + i);

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    regimeIVA: 'Normal',
    percentagemIVA: 16,
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// 🔥 DARK MODE — tokens de cor (light + dark)
// =============================================
function getTokens(isDark) {
    if (isDark) {
        return {
            bgCard: '#1c1c1e',
            bgFooter: '#2c2c2e',
            bgInput: '#2c2c2e',
            bgInputHover: '#3a3a3c',
            bgOption: '#2c2c2e',
            bgSection: 'rgba(255, 255, 255, 0.03)',
            bgHighlight: 'linear-gradient(135deg, rgba(129, 140, 248, 0.12), rgba(124, 58, 237, 0.06))',
            textPrimary: '#edeffd',
            textSecondary: 'rgba(255, 255, 255, 0.55)',
            textMuted: 'rgba(255, 255, 255, 0.4)',
            border: 'rgba(255, 255, 255, 0.08)',
            borderStrong: 'rgba(255, 255, 255, 0.15)',
            primary: '#818cf8',
            primaryGradient: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
            success: '#34d399',
            successHover: '#10b981',
            warning: '#fbbf24',
            danger: '#f87171',
            dangerHover: '#ef4444',
            overlay: 'rgba(0, 0, 0, 0.7)',
            headerGradient: 'linear-gradient(135deg, rgba(129, 140, 248, 0.08), rgba(124, 58, 237, 0.04))',
            btnSecondaryBg: 'rgba(255, 255, 255, 0.06)',
            btnSecondaryBgHover: 'rgba(255, 255, 255, 0.12)',
            shadowSm: '0 1px 3px rgba(0, 0, 0, 0.3)',
            shadowMd: '0 4px 12px rgba(0, 0, 0, 0.4)'
        };
    }
    return {
        bgCard: '#ffffff',
        bgFooter: '#f3f4f6',
        bgInput: '#ffffff',
        bgInputHover: '#f9fafb',
        bgOption: '#ffffff',
        bgSection: 'rgba(0, 0, 0, 0.02)',
        bgHighlight: 'linear-gradient(135deg, rgba(79, 70, 229, 0.06), rgba(124, 58, 237, 0.03))',
        textPrimary: '#1a1a2e',
        textSecondary: '#6b7280',
        textMuted: '#94a3b8',
        border: '#e5e7eb',
        borderStrong: '#d1d5db',
        primary: '#4f46e5',
        primaryGradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
        success: '#10b981',
        successHover: '#059669',
        warning: '#f59e0b',
        danger: '#dc2626',
        dangerHover: '#b91c1c',
        overlay: 'rgba(0, 0, 0, 0.6)',
        headerGradient: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(124, 58, 237, 0.03))',
        btnSecondaryBg: 'transparent',
        btnSecondaryBgHover: 'rgba(0, 0, 0, 0.04)',
        shadowSm: '0 1px 3px rgba(0, 0, 0, 0.06)',
        shadowMd: '0 4px 12px rgba(0, 0, 0, 0.08)'
    };
}

// =============================================
// 🔥 CONFIGURAÇÃO DOS RELATÓRIOS
// =============================================
const RELATORIOS = [
    {
        id: 'balancete',
        titulo: 'Balancete',
        desc: 'Receita / Despesa / Lucro do período',
        icone: 'assessment',
        periodos: ['Mensal', 'Trimestral', 'Anual'],
        periodosDefault: 'Mensal',
        regimesPermitidos: []
    },
    {
        id: 'iva',
        titulo: 'Apuramento IVA',
        desc: 'IVA detalhado por taxa ou período',
        icone: 'receipt_long',
        periodos: ['Mensal', 'Trimestral', 'Anual'],
        periodosDefault: 'Mensal',
        regimesPermitidos: ['Normal']
    },
    {
        id: 'ispc',
        titulo: 'Apuramento ISPC',
        desc: 'Volume de Negócios e imposto trimestral (15%)',
        icone: 'schedule',
        periodos: ['Trimestral', 'Anual'],
        periodosDefault: 'Trimestral',
        regimesPermitidos: ['ISPC']
    },
    {
        id: 'vencidas',
        titulo: 'Aging de Dívidas',
        desc: 'Clientes com valores em atraso (ano atual)',
        icone: 'warning',
        periodos: ['Anual'],
        periodosDefault: 'Anual',
        regimesPermitidos: []
    },
    {
        id: 'despesas',
        titulo: 'Lista de Despesas',
        desc: 'Todas as despesas do período',
        icone: 'receipt',
        periodos: ['Mensal', 'Trimestral', 'Anual'],
        periodosDefault: 'Mensal',
        regimesPermitidos: []
    }
];

function filtrarRelatoriosPorRegime(regime) {
    const regimeNormalizado = String(regime || 'Normal').trim();
    return RELATORIOS.filter(r => {
        if (!r.regimesPermitidos || r.regimesPermitidos.length === 0) return true;
        return r.regimesPermitidos.includes(regimeNormalizado);
    });
}

function labelPeriodo(periodo, mes, trimestre, ano) {
    if (periodo === 'Mensal') return `${meses[mes]} ${ano}`;
    if (periodo === 'Trimestral') {
        const mesesDoTri = {
            1: 'Jan, Fev, Mar',
            2: 'Abr, Mai, Jun',
            3: 'Jul, Ago, Set',
            4: 'Out, Nov, Dez'
        }[trimestre];
        return `T${trimestre}/${ano} (${mesesDoTri})`;
    }
    return `Ano ${ano}`;
}

function nomeFicheiro(tipo, periodo, ano, mes, trimestre, ext) {
    if (periodo === 'Mensal') {
        return `${tipo}_${ano}_${String(mes + 1).padStart(2, '0')}.${ext}`;
    }
    if (periodo === 'Trimestral') {
        return `${tipo}_${ano}_T${trimestre}.${ext}`;
    }
    return `${tipo}_${ano}.${ext}`;
}

function getToken() {
    return (
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        sessionStorage.getItem('token') ||
        null
    );
}

async function extrairErroDeResposta(error) {
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (data instanceof Blob) {
        try {
            const texto = await data.text();

            if (texto.trim().startsWith('<!DOCTYPE') || texto.trim().startsWith('<html')) {
                return `HTTP ${status}: O servidor devolveu HTML em vez de JSON/PDF — verifique se a rota está registada corretamente.`;
            }

            try {
                const json = JSON.parse(texto);
                const msg = json.erro || json.message || json.detalhe || json.error;
                if (msg) return `HTTP ${status}: ${msg}`;
            } catch { /* não é JSON */ }

            const limpo = texto.replace(/\s+/g, ' ').trim().slice(0, 300);
            return `HTTP ${status}: ${limpo || 'resposta vazia'}`;
        } catch {
            return `HTTP ${status}: resposta ilegível`;
        }
    }

    if (data && typeof data === 'object') {
        const msg = data.erro || data.message || data.detalhe || data.error;
        if (msg) return `HTTP ${status}: ${msg}`;
    }

    if (typeof data === 'string' && data.trim()) {
        return `HTTP ${status}: ${data.slice(0, 300)}`;
    }

    return `HTTP ${status || '???'}: ${error?.message || 'Erro desconhecido'}`;
}

// =============================================
// 🔥 HOOK — detetar dark mode
// =============================================
function useDarkMode() {
    const [isDark, setIsDark] = useState(() => {
        if (typeof document === 'undefined') return false;
        const root = document.documentElement;
        const body = document.body;
        return (
            root.classList.contains('dark-mode-variables') ||
            body.classList.contains('dark-mode-variables') ||
            root.classList.contains('dark-mode') ||
            body.classList.contains('dark-mode') ||
            localStorage.getItem('darkMode') === 'true' ||
            localStorage.getItem('tema') === 'dark'
        );
    });

    useEffect(() => {
        const check = () => {
            const root = document.documentElement;
            const body = document.body;
            setIsDark(
                root.classList.contains('dark-mode-variables') ||
                body.classList.contains('dark-mode-variables') ||
                root.classList.contains('dark-mode') ||
                body.classList.contains('dark-mode') ||
                localStorage.getItem('darkMode') === 'true' ||
                localStorage.getItem('tema') === 'dark'
            );
        };

        const observer = new MutationObserver(check);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        window.addEventListener('storage', check);

        return () => {
            observer.disconnect();
            window.removeEventListener('storage', check);
        };
    }, []);

    return isDark;
}

// =============================================
// 🔥 CSS INJETADO — hover, transition, dropdown styling
// =============================================
const CSS_OVERRIDES = `
.fin-exportacoes select option {
    background: var(--fin-option-bg, #ffffff);
    color: var(--fin-option-text, #1a1a2e);
    padding: 0.4rem 0.6rem;
}

.fin-exportacoes select {
    transition: all 0.2s ease;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 14px;
    padding-right: 2.2rem !important;
    cursor: pointer;
}

.fin-exportacoes select:hover:not(:disabled) {
    border-color: var(--fin-primary-hover, #6366f1) !important;
    background-color: var(--fin-input-hover-bg, #f9fafb) !important;
}

.fin-exportacoes select:focus:not(:disabled) {
    border-color: var(--fin-primary-hover, #6366f1) !important;
    box-shadow: 0 0 0 3px var(--fin-focus-ring, rgba(79, 70, 229, 0.12)) !important;
    outline: none;
}

.fin-exportacoes select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Botões do modal — hover */
.fin-exportacoes .fin-modal-btn {
    transition: all 0.2s ease;
}

.fin-exportacoes .fin-modal-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.fin-exportacoes .fin-modal-btn:active:not(:disabled) {
    transform: translateY(0);
}

/* Botões pill (período) — hover */
.fin-exportacoes .fin-pill-btn {
    transition: all 0.2s ease;
}

.fin-exportacoes .fin-pill-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
}

/* Botões tri (trimestre) — hover */
.fin-exportacoes .fin-tri-btn {
    transition: all 0.2s ease;
}

.fin-exportacoes .fin-tri-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
}

/* Botões de exportação — hover */
.fin-exportacoes .fin-export-btn {
    transition: all 0.2s ease;
}

.fin-exportacoes .fin-export-btn:hover:not(:disabled) {
    transform: translateY(-1px);
}

.fin-exportacoes .fin-export-btn:active:not(:disabled) {
    transform: translateY(0);
}

/* Botão fechar do modal */
.fin-exportacoes .fin-modal-close {
    transition: all 0.2s ease;
}

.fin-exportacoes .fin-modal-close:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06) !important;
    transform: rotate(90deg);
}

.dark-mode-variables .fin-exportacoes .fin-modal-close:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1) !important;
}
`;

export default function FinanceiroExportacoes() {
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);
    const [modalAberto, setModalAberto] = useState(false);
    const [relatorioSelecionado, setRelatorioSelecionado] = useState(null);
    const [periodo, setPeriodo] = useState('Mensal');
    const [mes, setMes] = useState(new Date().getMonth());
    const [trimestre, setTrimestre] = useState(trimestreDoMes(new Date().getMonth()));
    const [ano, setAno] = useState(new Date().getFullYear());
    const [exportando, setExportando] = useState(false);
    const [erroExportacao, setErroExportacao] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);

    const isDark = useDarkMode();
    const T = useMemo(() => getTokens(isDark), [isDark]);

    const isMountedRef = useRef(true);

    // 🔥 Injetar CSS para hover + dropdowns
    useEffect(() => {
        const styleId = 'fin-exportacoes-overrides';
        if (document.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = CSS_OVERRIDES;
        document.head.appendChild(style);

        return () => {
            const el = document.getElementById(styleId);
            if (el) el.remove();
        };
    }, []);

    // 🔥 Aplicar variáveis CSS para os dropdowns (option) conforme o tema
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--fin-option-bg', isDark ? '#2c2c2e' : '#ffffff');
        root.style.setProperty('--fin-option-text', isDark ? '#edeffd' : '#1a1a2e');
        root.style.setProperty('--fin-input-hover-bg', isDark ? '#3a3a3c' : '#f9fafb');
        root.style.setProperty('--fin-primary-hover', isDark ? '#a5b4fc' : '#6366f1');
        root.style.setProperty('--fin-focus-ring', isDark ? 'rgba(129, 140, 248, 0.2)' : 'rgba(79, 70, 229, 0.12)');
    }, [isDark]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData || !isMountedRef.current) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                regimeIVA: emp?.regimeIVA ?? prev.regimeIVA,
                percentagemIVA: emp?.percentagemIVA ?? prev.percentagemIVA,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    useEffect(() => {
        let cancelado = false;

        const carregarConfig = async () => {
            setLoadingConfig(true);
            try {
                const t = getToken();
                if (!t) {
                    console.warn('⚠️ Sem token — não é possível carregar configurações');
                    if (!cancelado) setLoadingConfig(false);
                    return;
                }

                const agora = new Date();
                const res = await axios.get(`${API}/financeiro/completo`, {
                    params: {
                        periodo: 'mes',
                        ano: agora.getFullYear(),
                        mes: agora.getMonth()
                    },
                    headers: { Authorization: `Bearer ${t}` }
                });

                if (!cancelado && res.data?.success) {
                    aplicarConfiguracao(res.data);
                }
            } catch (e) {
                if (!cancelado) {
                    console.warn('⚠️ Não foi possível carregar config da empresa:', e?.message);
                }
            } finally {
                if (!cancelado) setLoadingConfig(false);
            }
        };

        carregarConfig();

        return () => { cancelado = true; };
    }, [aplicarConfiguracao]);

    const regimeIVA = empresaConfig.regimeIVA || 'Normal';
    const isNormal = regimeIVA === 'Normal';
    const isISPC = regimeIVA === 'ISPC';
    const isIsento = regimeIVA === 'Isento';

    const relatoriosDisponiveis = useMemo(
        () => filtrarRelatoriosPorRegime(regimeIVA),
        [regimeIVA]
    );

    const abrirConfiguracao = (relatorio) => {
        setErroExportacao(null);
        setRelatorioSelecionado(relatorio);
        setPeriodo(relatorio.periodosDefault || relatorio.periodos[0] || 'Mensal');
        setMes(new Date().getMonth());
        setTrimestre(trimestreDoMes(new Date().getMonth()));
        setAno(new Date().getFullYear());
        setModalAberto(true);
    };

    const fecharModal = useCallback(() => {
        if (exportando) return;
        setModalAberto(false);
        setRelatorioSelecionado(null);
        setErroExportacao(null);
    }, [exportando]);

    const construirPayload = useCallback(() => {
        if (!relatorioSelecionado) return null;
        return {
            tipo: relatorioSelecionado.id,
            periodo,
            ano,
            mes: periodo === 'Mensal' ? mes : null,
            trimestre: periodo === 'Trimestral' ? trimestre : null
        };
    }, [relatorioSelecionado, periodo, ano, mes, trimestre]);

    const downloadBlob = useCallback((blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, 150);
    }, []);

    const exportarCSV = async () => {
        if (!relatorioSelecionado) {
            alert('Selecione um relatório primeiro');
            return;
        }

        const payload = construirPayload();
        if (!payload) return;

        setExportando(true);
        setErroExportacao(null);

        try {
            const t = getToken();
            if (!t) throw new Error('Sessão expirada. Faça login novamente.');

            console.log('📤 [exportarCSV] Payload:', payload);

            const res = await axios.post(
                `${API}/financeiro/exportar/csv`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${t}`,
                        'Content-Type': 'application/json'
                    },
                    responseType: 'blob',
                    validateStatus: (status) => status >= 200 && status < 300
                }
            );

            const filename = nomeFicheiro(
                relatorioSelecionado.id,
                periodo,
                ano,
                mes,
                trimestre,
                'csv'
            );

            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            downloadBlob(blob, filename);
            fecharModal();
        } catch (e) {
            console.error('❌ [exportarCSV]', e);
            const msg = await extrairErroDeResposta(e);
            if (isMountedRef.current) setErroExportacao(msg);
            alert('Erro ao exportar CSV:\n\n' + msg);
        } finally {
            if (isMountedRef.current) setExportando(false);
        }
    };

    const exportarPDF = async () => {
        if (!relatorioSelecionado) {
            alert('Selecione um relatório primeiro');
            return;
        }

        const payload = construirPayload();
        if (!payload) return;

        setExportando(true);
        setErroExportacao(null);

        try {
            const t = getToken();
            if (!t) throw new Error('Sessão expirada. Faça login novamente.');

            console.log('📤 [exportarPDF] Payload:', payload);

            const res = await axios.post(
                `${API}/financeiro/exportar/pdf`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${t}`,
                        'Content-Type': 'application/json'
                    },
                    responseType: 'blob',
                    validateStatus: (status) => status >= 200 && status < 300
                }
            );

            const filename = nomeFicheiro(
                relatorioSelecionado.id,
                periodo,
                ano,
                mes,
                trimestre,
                'pdf'
            );

            const blob = new Blob([res.data], { type: 'application/pdf' });
            downloadBlob(blob, filename);
            fecharModal();
        } catch (e) {
            console.error('❌ [exportarPDF]', e);
            const msg = await extrairErroDeResposta(e);
            if (isMountedRef.current) setErroExportacao(msg);
            alert('Erro ao exportar PDF:\n\n' + msg);
        } finally {
            if (isMountedRef.current) setExportando(false);
        }
    };

    const handlePeriodoChange = (p) => {
        setPeriodo(p);
        if (p === 'Mensal') {
            setMes(prev => (prev >= 0 && prev <= 11 ? prev : new Date().getMonth()));
        }
        if (p === 'Trimestral') {
            setTrimestre(prev => (prev >= 1 && prev <= 4 ? prev : trimestreDoMes(new Date().getMonth())));
        }
    };

    const badgeRegime = useMemo(() => {
        if (isISPC) {
            return { label: 'ISPC · IVA 0% + 15% Volume', cor: T.warning, bg: isDark ? 'rgba(251, 191, 36, 0.12)' : 'rgba(245, 158, 11, 0.1)' };
        }
        if (isIsento) {
            return { label: 'Isento · sem IVA nem ISPC', cor: T.textSecondary, bg: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(107, 114, 128, 0.1)' };
        }
        return { label: `Normal · IVA ${empresaConfig.percentagemIVA ?? 16}%`, cor: T.primary, bg: isDark ? 'rgba(129, 140, 248, 0.15)' : 'rgba(79, 70, 229, 0.1)' };
    }, [isISPC, isIsento, empresaConfig.percentagemIVA, T, isDark]);

    // =============================================
    // 🔥 ESTILOS REUTILIZÁVEIS (com dark mode + hover)
    // =============================================
    const labelStyle = {
        display: 'block',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: T.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '0.4rem'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.6rem 0.9rem',
        border: `1.5px solid ${T.border}`,
        borderRadius: '12px',  // 🔥 border-radius mais bonito
        background: T.bgInput,
        color: T.textPrimary,
        fontSize: '0.8rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        outline: 'none',
        transition: 'all 0.2s ease',  // 🔥 transição suave
        boxShadow: T.shadowSm
    };

    const pillButtonStyle = (isActive) => ({
        padding: '0.4rem 0.9rem',
        border: '1.5px solid',
        borderColor: isActive ? T.primary : T.border,
        background: isActive ? T.primary : 'transparent',
        color: isActive ? '#fff' : T.textPrimary,
        borderRadius: '100px',
        fontSize: '0.7rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: exportando ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isActive
            ? (isDark ? '0 2px 8px rgba(129, 140, 248, 0.3)' : '0 2px 8px rgba(79, 70, 229, 0.25)')
            : 'none'
    });

    const triButtonStyle = (isActive) => ({
        padding: '0.6rem 0.4rem',
        border: '1.5px solid',
        borderColor: isActive ? T.primary : T.border,
        background: isActive ? T.primary : 'transparent',
        color: isActive ? '#fff' : T.textPrimary,
        borderRadius: '12px',  // 🔥 border-radius mais bonito
        fontSize: '0.75rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: exportando ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isActive
            ? (isDark ? '0 2px 8px rgba(129, 140, 248, 0.3)' : '0 2px 8px rgba(79, 70, 229, 0.25)')
            : 'none'
    });

    return (
        <div className="fin-exportacoes">
            <header className="fin-section-header">
                <div className="fin-section-title">
                    <Icon name="download" size={24} color={T.primary} />
                    <div>
                        <h2>Exportações</h2>
                        <p>Relatórios em CSV / PDF — escolhe o período por relatório</p>
                    </div>
                </div>

                {!loadingConfig && (
                    <div style={{
                        padding: '0.35rem 0.75rem',
                        borderRadius: '100px',
                        background: badgeRegime.bg,
                        color: badgeRegime.cor,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        border: `1px solid ${badgeRegime.cor}30`
                    }}>
                        <Icon name="verified" size={12} color={badgeRegime.cor} />
                        {badgeRegime.label}
                    </div>
                )}
            </header>

            {loadingConfig ? (
                <div className="fin-loading">
                    <div className="fin-spinner" />
                    A carregar configurações...
                </div>
            ) : (
                <>
                    {relatoriosDisponiveis.length === 0 && (
                        <div className="fin-empty">
                            <Icon name="info" size={48} color={T.textMuted} />
                            <h3>Sem relatórios disponíveis</h3>
                            <p>O regime fiscal da sua empresa não tem relatórios configurados.</p>
                        </div>
                    )}

                    <section className="fin-export-grid">
                        {relatoriosDisponiveis.map(r => (
                            <div key={r.id} className="fin-export-card">
                                <div className="fin-export-icon">
                                    <Icon name={r.icone} size={28} color={T.primary} />
                                </div>
                                <h3>{r.titulo}</h3>
                                <p>{r.desc}</p>
                                <div className="fin-export-actions">
                                    <button
                                        className="fin-btn-primary"
                                        onClick={() => abrirConfiguracao(r)}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                                    >
                                        <Icon name="settings" size={14} color="#fff" />
                                        Configurar & Exportar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </section>
                </>
            )}

            {/* ===== MODAL DE CONFIGURAÇÃO ===== */}
            {modalAberto && relatorioSelecionado && (
                <div
                    className="modal-overlay"
                    onClick={fecharModal}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: T.overlay,
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        padding: '1rem'
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: T.bgCard,
                            color: T.textPrimary,
                            borderRadius: '16px',
                            maxWidth: '520px',
                            width: '100%',
                            boxShadow: isDark
                                ? '0 20px 60px rgba(0, 0, 0, 0.6)'
                                : '0 20px 60px rgba(0, 0, 0, 0.3)',
                            border: isDark ? `1px solid ${T.border}` : 'none',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '1.25rem 1.5rem',
                            borderBottom: `1px solid ${T.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: T.headerGradient
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    background: T.primaryGradient,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Icon name={relatorioSelecionado.icone} size={20} color="#fff" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: T.textPrimary }}>
                                        {relatorioSelecionado.titulo}
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '0.65rem', color: T.textSecondary }}>
                                        Escolhe o período e formato
                                    </p>
                                </div>
                            </div>
                            <button
                                className="fin-modal-close"
                                onClick={fecharModal}
                                disabled={exportando}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'transparent',
                                    color: T.textSecondary,
                                    cursor: exportando ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.2rem'
                                }}
                            >
                                <Icon name="close" size={18} color="currentColor" />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '1.25rem 1.5rem' }}>
                            {relatorioSelecionado.periodos.length > 1 && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={labelStyle}>
                                        Tipo de Período
                                    </label>
                                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                        {relatorioSelecionado.periodos.map(p => (
                                            <button
                                                key={p}
                                                className="fin-pill-btn"
                                                onClick={() => handlePeriodoChange(p)}
                                                disabled={exportando}
                                                style={pillButtonStyle(periodo === p)}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {periodo === 'Mensal' && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={labelStyle}>Mês</label>
                                    <select
                                        value={mes}
                                        onChange={e => setMes(parseInt(e.target.value))}
                                        disabled={exportando}
                                        style={inputStyle}
                                    >
                                        {meses.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                </div>
                            )}

                            {periodo === 'Trimestral' && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={labelStyle}>Trimestre</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem' }}>
                                        {[1, 2, 3, 4].map(tri => (
                                            <button
                                                key={tri}
                                                className="fin-tri-btn"
                                                onClick={() => setTrimestre(tri)}
                                                disabled={exportando}
                                                style={triButtonStyle(trimestre === tri)}
                                            >
                                                T{tri}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={labelStyle}>Ano</label>
                                <select
                                    value={ano}
                                    onChange={e => setAno(parseInt(e.target.value))}
                                    disabled={exportando}
                                    style={inputStyle}
                                >
                                    {ANOS_DISPONIVEIS.map(a => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>

                            {/* Resumo do período */}
                            <div style={{
                                padding: '0.75rem 1rem',
                                background: T.bgHighlight,
                                border: `1px solid ${isDark ? 'rgba(129, 140, 248, 0.25)' : 'rgba(79, 70, 229, 0.15)'}`,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6rem',
                                marginBottom: '1rem'
                            }}>
                                <Icon name="event" size={18} color={T.primary} />
                                <div>
                                    <div style={{
                                        fontSize: '0.55rem',
                                        fontWeight: 700,
                                        color: T.primary,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Período Selecionado
                                    </div>
                                    <strong style={{ fontSize: '0.85rem', color: T.textPrimary }}>
                                        {labelPeriodo(periodo, mes, trimestre, ano)}
                                    </strong>
                                </div>
                            </div>

                            {isISPC && relatorioSelecionado.id === 'ispc' && (
                                <div style={{
                                    padding: '0.6rem 0.9rem',
                                    background: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(245, 158, 11, 0.08)',
                                    border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.3)' : 'rgba(245, 158, 11, 0.25)'}`,
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    color: isDark ? '#fbbf24' : '#92400e',
                                    display: 'flex',
                                    gap: '0.5rem',
                                    alignItems: 'flex-start',
                                    marginBottom: '0.75rem'
                                }}>
                                    <Icon name="info" size={14} color={isDark ? '#fbbf24' : '#92400e'} />
                                    <span>
                                        No regime ISPC, o imposto (15%) é apurado trimestralmente
                                        sobre o Volume de Negócios acumulado.
                                    </span>
                                </div>
                            )}

                            {isNormal && relatorioSelecionado.id === 'iva' && (
                                <div style={{
                                    padding: '0.6rem 0.9rem',
                                    background: isDark ? 'rgba(129, 140, 248, 0.12)' : 'rgba(79, 70, 229, 0.06)',
                                    border: `1px solid ${isDark ? 'rgba(129, 140, 248, 0.3)' : 'rgba(79, 70, 229, 0.2)'}`,
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    color: isDark ? '#a5b4fc' : '#3730a3',
                                    display: 'flex',
                                    gap: '0.5rem',
                                    alignItems: 'flex-start',
                                    marginBottom: '0.75rem'
                                }}>
                                    <Icon name="info" size={14} color={isDark ? '#a5b4fc' : '#3730a3'} />
                                    <span>
                                        Apuramento de IVA a {empresaConfig.percentagemIVA ?? 16}% —
                                        IVA Liquidado menos IVA Dedutível.
                                    </span>
                                </div>
                            )}

                            {erroExportacao && (
                                <div style={{
                                    padding: '0.6rem 0.9rem',
                                    background: isDark ? 'rgba(248, 113, 113, 0.12)' : 'rgba(220, 38, 38, 0.08)',
                                    border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.3)' : 'rgba(220, 38, 38, 0.3)'}`,
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    color: isDark ? '#f87171' : '#991b1b',
                                    display: 'flex',
                                    gap: '0.5rem',
                                    alignItems: 'flex-start',
                                    wordBreak: 'break-word'
                                }}>
                                    <Icon name="error" size={14} color={isDark ? '#f87171' : '#991b1b'} />
                                    <span>{erroExportacao}</span>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '1rem 1.5rem',
                            background: T.bgFooter,
                            borderTop: `1px solid ${T.border}`,
                            display: 'flex',
                            gap: '0.6rem',
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                className="fin-modal-btn"
                                onClick={fecharModal}
                                disabled={exportando}
                                style={{
                                    padding: '0.6rem 1.4rem',
                                    border: `1.5px solid ${T.border}`,
                                    background: T.btnSecondaryBg,
                                    color: T.textSecondary,
                                    borderRadius: '100px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    fontFamily: 'inherit',
                                    cursor: exportando ? 'not-allowed' : 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    if (!exportando) {
                                        e.currentTarget.style.background = T.btnSecondaryBgHover;
                                        e.currentTarget.style.color = T.textPrimary;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = T.btnSecondaryBg;
                                    e.currentTarget.style.color = T.textSecondary;
                                }}
                            >
                                Cancelar
                            </button>

                            <button
                                className="fin-modal-btn fin-export-btn"
                                onClick={exportarCSV}
                                disabled={exportando}
                                style={{
                                    padding: '0.6rem 1.4rem',
                                    border: 'none',
                                    background: T.success,
                                    color: '#fff',
                                    borderRadius: '100px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    fontFamily: 'inherit',
                                    cursor: exportando ? 'not-allowed' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    boxShadow: isDark
                                        ? '0 2px 10px rgba(52, 211, 153, 0.3)'
                                        : '0 2px 10px rgba(16, 185, 129, 0.25)',
                                    opacity: exportando ? 0.7 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!exportando) {
                                        e.currentTarget.style.background = T.successHover;
                                        e.currentTarget.style.boxShadow = isDark
                                            ? '0 4px 16px rgba(52, 211, 153, 0.45)'
                                            : '0 4px 16px rgba(16, 185, 129, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = T.success;
                                    e.currentTarget.style.boxShadow = isDark
                                        ? '0 2px 10px rgba(52, 211, 153, 0.3)'
                                        : '0 2px 10px rgba(16, 185, 129, 0.25)';
                                }}
                            >
                                <Icon name="download" size={14} color="#fff" />
                                {exportando ? 'A exportar...' : 'Exportar CSV'}
                            </button>

                            <button
                                className="fin-modal-btn fin-export-btn"
                                onClick={exportarPDF}
                                disabled={exportando}
                                style={{
                                    padding: '0.6rem 1.4rem',
                                    border: 'none',
                                    background: T.primaryGradient,
                                    color: '#fff',
                                    borderRadius: '100px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    fontFamily: 'inherit',
                                    cursor: exportando ? 'not-allowed' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    boxShadow: isDark
                                        ? '0 2px 10px rgba(129, 140, 248, 0.3)'
                                        : '0 2px 10px rgba(79, 70, 229, 0.25)',
                                    opacity: exportando ? 0.7 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!exportando) {
                                        e.currentTarget.style.boxShadow = isDark
                                            ? '0 4px 16px rgba(129, 140, 248, 0.5)'
                                            : '0 4px 16px rgba(79, 70, 229, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = isDark
                                        ? '0 2px 10px rgba(129, 140, 248, 0.3)'
                                        : '0 2px 10px rgba(79, 70, 229, 0.25)';
                                }}
                            >
                                <Icon name="description" size={14} color="#fff" />
                                {exportando ? 'A gerar...' : 'Exportar PDF'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}